import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  brand,
  radius,
  semantic,
  shadow,
  space,
  typeRole,
  weight,
  zIndex,
} from '@repo/design-tokens';
import { Icon } from '@repo/design-system';
import { visuallyHidden } from '@repo/ui';

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';
/** Holds a control's own tabindex ('' for none) while the closed panel takes it out of the order. */
const PARKED = 'data-notices-tabindex';

/**
 * The draft notices as a chip in the one-bar header (`observer-autogen-review-layout`, L2): what the
 * orange block above the stage said — failures, the draft-or-running summary, skipped measures —
 * now opens from one chip, so it no longer takes the picture's height.
 *
 * A disclosure rather than a portalled popover, on purpose: the notices carry the step's live
 * regions (`LiveConfigurationNotices`), and a region must stay mounted to be announced. So the panel
 * is always rendered — unseen while closed — and the chip only shows or hides it. Nothing is lost:
 * every notice, its detail and Retry are in the panel, after the step's launch note.
 */
const NoticesChip: React.FC<{
  /** The step's `LiveConfigurationNotices` element, rendered once, inside the panel. */
  children: React.ReactNode;
  /** When the step's changes take effect — the title row's old note, now the panel's first line. */
  launchNote?: string;
  className?: string;
}> = ({ children, launchNote, className }) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const chipRef = useRef<HTMLButtonElement | null>(null);
  const panelId = useId();

  /*
   * How many notices show, read from what the notices drew: each one is a design-system Banner,
   * which carries `role="alert"` (errors) or `role="status"` (the rest). Read after every render, so
   * the chip's count can never disagree with the panel it opens.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs after every render by design; its setters are equality-guarded
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const banners = panel.querySelectorAll(
      '[data-notices-body] [role="alert"], [data-notices-body] [role="status"]',
    );
    const shown = Array.from(banners).filter(el => (el.textContent ?? '').trim().length > 0);
    const errors = shown.filter(el => el.getAttribute('role') === 'alert').length;
    if (shown.length !== count) setCount(shown.length);
    const anyError = errors > 0;
    if (anyError !== hasError) setHasError(anyError);
    /*
     * Closed, the panel is unseen but mounted (its regions must be), so its controls — a notice's
     * Retry — leave the tab order: Tab never lands on a button nobody can see. Open, they return.
     */
    panel.querySelectorAll<HTMLElement>(FOCUSABLE).forEach(el => {
      if (!open) {
        if (!el.hasAttribute(PARKED)) el.setAttribute(PARKED, el.getAttribute('tabindex') ?? '');
        el.setAttribute('tabindex', '-1');
      } else if (el.hasAttribute(PARKED)) {
        const was = el.getAttribute(PARKED);
        if (was) el.setAttribute('tabindex', was);
        else el.removeAttribute('tabindex');
        el.removeAttribute(PARKED);
      }
    });
  });

  // Nothing to show: the panel closes, and the chip goes (the regions stay, unseen).
  useEffect(() => {
    if (count === 0 && open) setOpen(false);
  }, [count, open]);

  // A press outside the chip and its panel closes it; focus is not moved (the press chose a target).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (t && (panelRef.current?.contains(t) || chipRef.current?.contains(t))) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const label = `Draft notices, ${count}`;

  return (
    <span className={className}>
      {count > 0 && (
        <button
          ref={chipRef}
          type="button"
          className={`chip${hasError ? ' error' : ''}${open ? ' open' : ''}`}
          aria-label={label}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen(o => !o)}
          onKeyDown={e => {
            // Opened from the keyboard, focus stays here: Esc closes it from here too.
            if (e.key !== 'Escape' || !open) return;
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }}
        >
          <Icon name={hasError ? 'circle-exclamation' : 'circle-info'} size="xs" />
          <span aria-hidden="true">Draft notices · {count}</span>
          <Icon name="chevron-down" variant="solid" size="xs" />
        </button>
      )}
      <div
        id={panelId}
        ref={panelRef}
        className="panel"
        role="region"
        aria-label="Draft notices"
        style={open ? undefined : visuallyHidden}
        onKeyDown={e => {
          if (e.key !== 'Escape') return;
          e.stopPropagation();
          setOpen(false);
          chipRef.current?.focus();
        }}
      >
        {launchNote && <p className="launch-note">{launchNote}</p>}
        <div data-notices-body="">{children}</div>
      </div>
    </span>
  );
};

export default styled(NoticesChip)`
  position: relative;
  display: inline-flex;
  flex: none;

  .chip {
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
    padding: ${space[1]} ${space[3]};
    border: 1px solid ${semantic.border.default};
    border-radius: ${radius.pill};
    background: ${semantic.surface.base};
    color: ${semantic.text.primary};
    font: inherit;
    font-size: ${typeRole.caption1.fontSize};
    font-weight: ${weight.semibold};
    cursor: pointer;

    &.error {
      border-color: ${semantic.feedback.error};
      color: ${semantic.text.error};
    }
    &.open {
      box-shadow: ${shadow.glassHover};
    }
    &:focus-visible {
      outline: none;
      box-shadow: ${brand.focusRing};
    }
  }

  .panel {
    position: absolute;
    z-index: ${zIndex.popover};
    top: calc(100% + ${space[2]});
    right: 0;
    width: min(560px, calc(100vw - 2 * ${space[4]}));
    max-height: 70vh;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: ${space[2]};
    padding: ${space[3]};
    border-radius: ${radius.lg};
    background: ${semantic.surface.base};
    box-shadow: ${shadow.glass};
  }

  .launch-note {
    margin: 0;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${semantic.text.secondary};
  }
`;
